import { m } from "framer-motion";
import React, { createContext, useContext, useState, useEffect } from "react";

// Toaster Component
interface PillToasterProps {
  open: boolean;
  message: string;
  type?: "success" | "error" | "warning" | "info";
  onClose?: () => void;
  duration?: number;
  position?: "top" | "bottom";
}

const PillToaster: React.FC<PillToasterProps> = ({
  open,
  message,
  type = "info",
  onClose,
  duration = 5000,
  position = "top",
}) => {
  const [isVisible, setIsVisible] = useState(open);
  if (!isVisible) return null;

  useEffect(() => {
    setIsVisible(open);
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration]);

  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose?.();
    }, 400);
  };

  const configs = {
    success: {
      background: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
      label: "Done",
      labelBg: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
    },
    error: {
      background: "linear-gradient(90deg, #F43F5E 0%, #DC2626 100%)",
      label: "Error",
      labelBg: "linear-gradient(90deg, #F43F5E 0%, #DC2626 100%)",
    },
    warning: {
      background: "linear-gradient(90deg, #FBBF24 0%, #F59E0B 100%)",
      label: "Warning",
      labelBg: "linear-gradient(90deg, #FBBF24 0%, #F59E0B 100%)",
    },
    info: {
      background: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
      label: "OK",
      labelBg: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
    },
  };

  const config = configs[type] || configs.info;

  const positionStyles =
    position === "top" ? { top: "30px" } : { bottom: "30px" };
  if (!message) return null;
  return (
    <>
      <div
        style={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          ...positionStyles,
          zIndex: 30000,
          animation:
            position === "top"
              ? "slideDown 0.3s ease-out"
              : "slideUp 0.3s ease-out",
          filter: "drop-shadow(0px 10px 30px rgba(0, 0, 0, 0.18))",
        }}
      >
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #DADCDF",
            borderRadius: "25px",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            minHeight: "50px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#000000",
              lineHeight: "1.5",
              whiteSpace: "pre-line",
              maxWidth: "90vw",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              display: "block",
              // minWidth: "100px",
              minWidth: `${Math.min(
                600,
                Math.max(100, message?.length * 8)
              )}px`,
            }}
          >
            {message}
          </span>

          {config.label && (
            <div
              style={{
                background: config.labelBg,
                borderRadius: "13px",
                padding: "6px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "52px",
                height: "26px",
              }}
              onClick={handleClose}
            >
              <span
                style={{
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#FFFFFF",
                  lineHeight: "1",
                }}
              >
                {config.label}
              </span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateX(-50%) translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateX(-50%) translateY(0); 
          }
        }
        @keyframes slideDown {
          from { 
            opacity: 0; 
            transform: translateX(-50%) translateY(-20px); 
          }
          to { 
            opacity: 1; 
            transform: translateX(-50%) translateY(0); 
          }
        }
      `}</style>
    </>
  );
};

// Context
interface ToasterContextType {
  showToaster: (
    message: string,
    type?: "success" | "error" | "warning" | "info",
    duration?: number,
    position?: "top" | "bottom"
  ) => void;
  success: (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => void;
  error: (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => void;
  warning: (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => void;
  info: (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => void;
  hideToaster: () => void;
}

const ToasterContext = createContext<ToasterContextType | undefined>(undefined);

// Provider
export const ToasterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toaster, setToaster] = useState({
    open: false,
    message: "",
    type: "info" as "success" | "error" | "warning" | "info",
    duration: 5000,
    position: "top" as "top" | "bottom",
  });

  const showToaster = (
    message: string,
    type: "success" | "error" | "warning" | "info" = "info",
    duration = 5000,
    position: "top" | "bottom" = "top"
  ) => {
    setToaster({ open: true, message, type, duration, position });
  };

  const hideToaster = () => {
    setToaster((prev) => ({ ...prev, open: false }));
  };

  const success = (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => showToaster(message, "success", duration, position);

  const error = (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => showToaster(message, "error", duration, position);

  const warning = (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => showToaster(message, "warning", duration, position);

  const info = (
    message: string,
    duration?: number,
    position?: "top" | "bottom"
  ) => showToaster(message, "info", duration, position);

  return (
    <ToasterContext.Provider
      value={{ showToaster, success, error, warning, info, hideToaster }}
    >
      {children}
      {toaster.open && (
        <PillToaster
          open={toaster.open}
          message={toaster.message}
          type={toaster.type}
          duration={toaster.duration}
          position={toaster.position}
          onClose={hideToaster}
        />
      )}
    </ToasterContext.Provider>
  );
};

// Hook
export const useToaster = () => {
  const context = useContext(ToasterContext);
  if (!context) {
    throw new Error("useToaster must be used within a ToasterProvider");
  }
  return context;
};
